<template>
  <div>
    <p
        v-if="item.description"
        class="mt-2 text-sm leading-5 text-gray-500 whitespace-pre-wrap"
        v-text="item.description"
    ></p>

    <ui-form-section class="mt-6">
      <ui-input label="Quantity" name="quantity" v-model="item.quantity" @change="estimate.cannot_be_edited_by_authed_user ? null : save" class="sm:col-span-6" :disabled="estimate.cannot_be_edited_by_authed_user"></ui-input>
      <ui-input label="Unit of Measure" name="uom" v-model="item.uom" @change="estimate.cannot_be_edited_by_authed_user ? null : save" class="sm:col-span-6" :disabled="estimate.cannot_be_edited_by_authed_user"></ui-input>
    </ui-form-section>

    <!-- Header. -->
    <div class="mt-6 flex w-full text-sm text-gray-700 font-medium">
      <div class="flex-1 flex-shrink-0"></div>
      <div class="w-1/3 flex-shrink-0 text-right px-2 py-1">Rate</div>
      <div class="w-1/3 flex-shrink-0 text-right px-2 py-1">Total</div>
    </div>

    <!-- Material cost. -->
    <div v-if="item.is_group_item" class="flex w-full border-t border-b border-l -mt-px border-gray-200 rounded-t-md">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2 rounded-tl-md">Material Cost</div>
      <div class="w-1/3 flex-shrink-0 -mr-px flex">
        <div
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50"
        >{{ specsDetails.rates.materials | toCurrency }}</div>
      </div>
      <div class="w-1/3 flex-shrink-0 flex">
        <div
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50 rounded-tr-md"
        >{{ specsDetails.amounts.materials | toCurrency }}</div>
      </div>
    </div>

    <!-- Labour cost. -->
    <div v-if="item.is_group_item" class="flex w-full border-t border-b border-l -mt-px border-gray-200">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2">Labour Cost</div>
      <div class="w-1/3 flex-shrink-0 -mr-px flex">
        <div
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50"
        >{{ specsDetails.rates.labour_rates | toCurrency }}</div>
      </div>
      <div class="w-1/3 flex-shrink-0 flex">
        <div
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50"
        >{{ specsDetails.amounts.labour_rates | toCurrency }}</div>
      </div>
    </div>

    <!-- Total Cost. -->
    <div class="flex w-full border-t border-b border-l -mt-px border-gray-200" :class="item.is_group_item ? '' : 'rounded-t-md'">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2" :class="item.is_group_item ? '' : 'rounded-tl-md'">
        {{ item.is_group_item ? 'Total Cost' : 'Cost' }}
      </div>
      <div class="w-1/3 flex-shrink-0 -mr-px flex">
        <div
            v-if="item.is_group_item"
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50"
            :class="item.is_group_item && item.source_of_truth_cost === 'rate' ? 'font-semibold' : ''"
        >{{ item.rate | toCurrency }}</div>
        <estimates-old-page-cell
            v-else
            currency
            class="text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200"
            :class="isSourceOfTruth('cost', 'rate') ? 'bg-gray-50' : ''"
            placeholder="Rate"
            v-model="item.rate"
            @change="saveCost('rate')"
        ></estimates-old-page-cell>
      </div>
      <div class="w-1/3 flex-shrink-0 flex">
        <div
            v-if="item.is_group_item"
            class="w-full text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200 bg-gray-50"
            :class="[
                        item.is_group_item && item.source_of_truth_cost === 'amount' ? 'font-semibold' : '',
                        item.is_group_item ? '' : 'rounded-tr-md',
                    ]"
        >{{ item.amount | toCurrency }}</div>
        <estimates-old-page-cell
            v-else
            currency
            class="text-sm text-right -my-px px-2 py-1.5 border border-gray-200 rounded-tr-md"
            :class="isSourceOfTruth('cost', 'amount') ? 'bg-gray-50' : ''"
            placeholder="Subtotal"
            v-model="item.amount"
            @change="saveCost('amount')"
        ></estimates-old-page-cell>
      </div>
    </div>

    <!-- Markup Percentage. -->
    <div class="flex w-full border-t border-b border-l -mt-px border-gray-200">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2">Markup Percentage</div>
      <div class="w-2/3 flex-shrink-0 pl-px flex relative">
        <estimates-old-page-cell
            class="text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200"
            :class="(isSourceOfTruth('markup', 'percentage') || isSourceOfTruth('markup', 'default')) ? 'bg-gray-50' : ''"
            placeholder="Markup amount"
            v-model="item.markup_pc"
            @change="saveMarkup('percentage', item.markup_pc)"
            :format="value => `${value}%`"
        ></estimates-old-page-cell>
        <div v-if="hasNonDefaultMarkup" class="absolute left-0 inset-y-0 flex px-2 items-center z-30">
          <button
              class="text-xs font-medium px-1 py-0.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 shadow-sm"
              @click="saveMarkup('default', '')"
          >Use Default Markup</button>
        </div>
      </div>
    </div>

    <!-- Markup. -->
    <div class="flex w-full border-t border-b border-l -mt-px border-gray-200">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2">Markup</div>
      <div class="w-1/3 flex-shrink-0 -mr-px flex">
        <estimates-old-page-cell
            currency
            class="text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200"
            :class="isSourceOfTruth('markup', 'rate') ? 'bg-gray-50' : ''"
            placeholder="Markup amount"
            v-model="item.markup_rate"
            @change="saveMarkup('rate', item.markup_rate)"
        ></estimates-old-page-cell>
      </div>
      <div class="w-1/3 flex-shrink-0 flex">
        <estimates-old-page-cell
            currency
            class="text-sm text-right -my-px px-2 py-1.5 border border-gray-200"
            :class="isSourceOfTruth('markup', 'amount') ? 'bg-gray-50' : ''"
            placeholder="Markup amount"
            v-model="item.markup_amount"
            @change="saveMarkup('amount', item.markup_amount)"
        ></estimates-old-page-cell>
      </div>
    </div>

    <!-- Total. -->
    <div class="flex w-full border-t border-b border-l -mt-px border-gray-200 rounded-b-md">
      <div class="flex-1 flex-shrink-0 text-gray-600 text-sm flex items-center px-2 rounded-bl-md font-semibold">Total</div>
      <div class="w-1/3 flex-shrink-0 -mr-px flex">
        <estimates-old-page-cell
            currency
            class="text-sm text-right -mr-px -my-px px-2 py-1.5 border border-gray-200"
            :class="isSourceOfTruth('cf', 'rate') ? 'bg-gray-50' : ''"
            placeholder="Marked-up Rate"
            v-model="item.cf_rate"
            @change="saveCustomerFacing('rate')"
        ></estimates-old-page-cell>
      </div>
      <div class="w-1/3 flex-shrink-0 flex">
        <estimates-old-page-cell
            currency
            class="text-sm text-right -my-px px-2 py-1.5 border border-gray-200 rounded-br-md"
            :class="isSourceOfTruth('cf', 'amount') ? 'bg-gray-50' : ''"
            placeholder="Total"
            v-model="item.cf_amount"
            @change="saveCustomerFacing('amount')"
        ></estimates-old-page-cell>
      </div>
    </div>

  </div>

</template>

<script>
import { estimateStore } from '@stores'

export default {
  props: {
    item: Object,
    specsDetails: Object,
  },
  computed: {
    $store () {
      return estimateStore
    },
    estimate () {
      return this.$store.state.estimate
    },
    hasNonDefaultMarkup () {
      return this.item.source_of_truth_markup !== 'default'
          || this.item.computation_strategy === 'markup'
    },
  },
  methods: {
    isSourceOfTruth (strategy, source) {
      if (this.item.computation_strategy === strategy) return false
      const sourceVariable = 'source_of_truth_' + strategy
      return this.item[sourceVariable] === source
    },
    saveCost (sourceOfTruth) {
      if (this.item.is_group_item) return
      this.item.source_of_truth_cost = sourceOfTruth
      if (this.item.computation_strategy === 'cost') {
        this.item.computation_strategy = 'markup'
      }
      this.save()
    },
    saveMarkup (sourceOfTruth, value) {
      this.item.source_of_truth_markup = value === '' ? 'default' : sourceOfTruth
      if (this.item.is_group_item) {
        this.item.computation_strategy = 'cf'
      } else if (this.item.computation_strategy === 'markup') {
        this.item.computation_strategy = 'cf'
      }
      this.save()
    },
    saveCustomerFacing (sourceOfTruth) {
      this.item.source_of_truth_cf = sourceOfTruth
      if (this.item.is_group_item) {
        this.item.computation_strategy = 'markup'
      } else if (this.item.computation_strategy === 'cf') {
        this.item.computation_strategy = 'cost'
      }
      this.save()
    },
    save () {
      this.$store.recalculateItem(this.item, true)
      this.$store.save()
    }
  }
}
</script>
